import { useState, useCallback } from "react";
import { getDefaultPhotoIndex, setDefaultPhoto } from "utils/document/productServicePhotos";

/**
 * Shared photo state for Product Service forms.
 *
 * Provides a consistent contract for managing a multi-image carousel:
 *   - `photos` / `activePhotoIndex` — single source of truth
 *   - `initPhotos(nextPhotos)` — load photos and sync index from default_value flags
 *   - `setPhotosAndIndex(nextPhotos, index?)` — update photos and explicitly set index
 *   - `getPhotosForSave()` — returns photos with default_value applied for activePhotoIndex
 *
 * The hook does NOT own any identity-based useEffect; callers are responsible for
 * calling `initPhotos` from their own useEffect when the product identity changes.
 */
export default function useProductServicePhotoState() {
  const [photos, setPhotos] = useState([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  /**
   * Initialize from a pre-normalized photos array (caller normalizes with context-specific fn).
   * Reads the default_value flag to restore the previously selected index.
   */
  const initPhotos = useCallback((nextPhotos) => {
    const normalized = Array.isArray(nextPhotos) ? nextPhotos : [];
    setPhotos(normalized);
    setActivePhotoIndex(getDefaultPhotoIndex(normalized));
  }, []);

  /**
   * Called when the carousel reports a change (navigation or upload).
   * The carousel already sets default_value on the active entry, so we read it back.
   */
  const onPhotosChange = useCallback((updatedPhotos) => {
    setPhotos(updatedPhotos);
    setActivePhotoIndex(getDefaultPhotoIndex(updatedPhotos));
  }, []);

  /**
   * Returns a copy of photos with default_value applied for activePhotoIndex.
   * Use before the upload loop so the persisted selection survives save.
   */
  const getPhotosForSave = useCallback(
    () => setDefaultPhoto(photos, activePhotoIndex),
    [photos, activePhotoIndex]
  );

  return {
    photos,
    setPhotos,
    activePhotoIndex,
    setActivePhotoIndex,
    initPhotos,
    onPhotosChange,
    getPhotosForSave,
  };
}
